import React, { useState, useMemo, useCallback } from 'react';
import {
  BarChart, Bar, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';
import { Clock, DollarSign, Activity, ChevronLeft, ChevronDown, ChevronRight, Zap, Target, X, Search, Plus, Trash2, Calendar } from 'lucide-react';
import { T, fmtMoney, fmtNum, safeNum } from '../theme';
import { Card, StatCard, CoBadge, ExportBtn, exportRawCSV } from '../components/UI';

const TIME_KEYS = ["1","2","3","4","5","10","20","30","60","120","180","300","600"];
const PRICE_KEYS = ["0_01","0_02","0_03","0_04","0_05","0_10","0_20","0_30","0_40","0_50","1_00"];
const TIME_LABELS = {"1":"1s","2":"2s","3":"3s","4":"4s","5":"5s","10":"10s","20":"20s","30":"30s","60":"1m","120":"2m","180":"3m","300":"5m","600":"10m"};
const PRICE_LABELS = {"0_01":"$0.01","0_02":"$0.02","0_03":"$0.03","0_04":"$0.04","0_05":"$0.05","0_10":"$0.10","0_20":"$0.20","0_30":"$0.30","0_40":"$0.40","0_50":"$0.50","1_00":"$1.00"};
const fmtK = v => { if (v == null || isNaN(v)) return "0"; const a = Math.abs(v); if (a >= 1e6) return (v/1e6).toFixed(1)+"M"; if (a >= 1e3) return (v/1e3).toFixed(1)+"K"; return v.toFixed(0); };

const EXCL_KEY = "cp_covering_exclude";
const EXCL_DAYS_KEY = "cp_covering_exclude_days";
const loadExclude = () => { try { return JSON.parse(localStorage.getItem(EXCL_KEY) || "[]"); } catch { return []; } };
const saveExclude = list => localStorage.setItem(EXCL_KEY, JSON.stringify(list));
const loadExclDays = () => { try { return JSON.parse(localStorage.getItem(EXCL_DAYS_KEY) || '{"from":0,"to":0}'); } catch { return { from: 0, to: 0 }; } };
const saveExclDays = d => localStorage.setItem(EXCL_DAYS_KEY, JSON.stringify(d));

const CTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (<div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 11 }}>
    <div style={{ fontWeight: 700, marginBottom: 4, color: T.text }}>{label}</div>
    {payload.map((p, i) => (<div key={i} style={{ color: p.color, display: "flex", justifyContent: "space-between", gap: 16 }}><span>{p.name}</span><span className="mono" style={{ fontWeight: 700 }}>{fmtMoney(p.value)}</span></div>))}
  </div>);
};

const findBest = (plMap, keys) => { let b = { key: null, pl: -Infinity }; keys.forEach(k => { const v = plMap[k] || 0; if (v > b.pl) b = { key: k, pl: v }; }); return b; };
const TH = { padding: "10px 8px", fontSize: 10, color: T.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, borderBottom: `2px solid ${T.border}`, whiteSpace: "nowrap", textAlign: "center" };
const TD = { padding: "10px 8px", fontSize: 12, textAlign: "center", verticalAlign: "middle" };

const Toggle = ({ items, value, onChange }) => (
  <div style={{ display: "flex", gap: 4 }}>
    {items.map(o => (<button key={o.id} onClick={() => onChange(o.id)}
      style={{ padding: "5px 14px", borderRadius: 6, border: `1px solid ${value === o.id ? o.color || "#fbbf24" : T.border}`, background: value === o.id ? (o.color || "#fbbf24") + "20" : "transparent", color: value === o.id ? (o.color || "#fbbf24") : T.textMuted, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
      {o.label}
    </button>))}
  </div>
);

// Sortable header helper
const SortHead = ({ label, col, sort, setSort, style }) => {
  const active = sort.col === col;
  return (<th onClick={() => setSort(s => s.col === col ? { col, dir: s.dir === "asc" ? "desc" : "asc" } : { col, dir: "desc" })}
    style={{ ...TH, cursor: "pointer", userSelect: "none", color: active ? T.cyan : T.textMuted, ...style }}>
    {label} {active && <span style={{ fontSize: 7 }}>{sort.dir === "asc" ? "▲" : "▼"}</span>}
  </th>);
};

// Generic sort function
const doSort = (arr, sort, getVal) => {
  if (!sort.col) return arr;
  return [...arr].sort((a, b) => {
    const av = getVal(a, sort.col), bv = getVal(b, sort.col);
    if (typeof av === "string") return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    return sort.dir === "asc" ? av - bv : bv - av;
  });
};

export default function CoveringGold({ coveringData: CD, profilingData: C, companyFilter, onClientClick }) {
  const [tab, setTab] = useState("overview");
  const [typeFilter, setTypeFilter] = useState("all");
  const [hmMode, setHmMode] = useState("time");
  const [selectedLogin, setSelectedLogin] = useState(null);
  const [clientSort, setClientSort] = useState({ col: "bestTimePL", dir: "desc" });
  const [hmSort, setHmSort] = useState({ col: "totalVolume", dir: "desc" });
  const [tradeSort, setTradeSort] = useState({ col: null, dir: "desc" });
  const [detailChartMode, setDetailChartMode] = useState("time");
  const [detailTradeMode, setDetailTradeMode] = useState("time");
  const [bsChartMode, setBsChartMode] = useState("time");
  const [tradePage, setTradePage] = useState(0);
  const [bestMode, setBestMode] = useState("time");
  const [bestExpanded, setBestExpanded] = useState(null);
  const [fCountry, setFCountry] = useState("");
  const [fAM, setFAM] = useState("");
  const [fSearch, setFSearch] = useState("");
  const [excludeList, setExcludeList] = useState(loadExclude);
  const [exclDays, setExclDays] = useState(loadExclDays);
  const [exclInput, setExclInput] = useState("");
  const [navHistory, setNavHistory] = useState([]);
  // Month/Year filters
  const [covYear, setCovYear] = useState("all");
  const [covMonth, setCovMonth] = useState("all");
  const [groupFilter, setGroupFilter] = useState([]);

  const updateExclude = useCallback(nl => { setExcludeList(nl); saveExclude(nl); }, []);
  const updateExclDays = useCallback(d => { setExclDays(d); saveExclDays(d); }, []);

  // Navigate to client detail, remembering where we came from
  const goToClient = useCallback((key, fromTab) => {
    setNavHistory(h => [...h, { tab: fromTab || tab, selectedLogin }]);
    setSelectedLogin(key);
    setTab("clients");
    setTradePage(0);
    setTradeSort({ col: null, dir: "desc" });
  }, [tab, selectedLogin]);

  // Go back to previous state
  const goBack = useCallback(() => {
    if (navHistory.length > 0) {
      const prev = navHistory[navHistory.length - 1];
      setNavHistory(h => h.slice(0, -1));
      setTab(prev.tab);
      setSelectedLogin(prev.selectedLogin);
    } else {
      setSelectedLogin(null);
    }
  }, [navHistory]);

  if (!CD || !CD.trades?.length) {
    return (<div style={{ textAlign: "center", padding: "80px 20px" }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🥇</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 8 }}>Covering Gold</div>
      <div style={{ color: T.textDim, fontSize: 13, maxWidth: 400, margin: "0 auto" }}>No covering data loaded. Go to Admin → Covering Gold to upload.</div>
    </div>);
  }

  const profileMap = useMemo(() => {
    const m = {};
    // Use perClientLatest (latest row per company+login across ALL months) so both companies always resolve
    if (C?.perClientLatest) Object.entries(C.perClientLatest).forEach(([k, c]) => { m[k] = c; });
    // Also layer healthScores on top (has computed fields like healthScore, _clientGroup)
    if (C?.healthScores) C.healthScores.forEach(c => { m[c._company + "|" + c.Login] = c; });
    return m;
  }, [C]);

  const exclSet = useMemo(() => new Set(excludeList.map(e => String(e).toLowerCase().trim())), [excludeList]);

  // Available years/months from all covering data
  const covOptions = useMemo(() => {
    const years = [...new Set(CD.trades.map(t => t.Year).filter(Boolean))].sort();
    const months = [...new Set(CD.trades.map(t => t.Month).filter(Boolean))].sort();
    return { years, months };
  }, [CD.trades]);

  // Available groups from profiling data
  const availableGroups = useMemo(() => {
    const grpSet = new Set();
    CD.trades.forEach(t => {
      const p = profileMap[(t._company || "") + "|" + t.Login];
      grpSet.add(p?._clientGroup || "Unassigned");
    });
    return [...grpSet].sort();
  }, [CD.trades, profileMap]);

  const filteredTrades = useMemo(() => CD.trades.filter(t => {
    // Company filter from sidebar
    if (companyFilter && companyFilter !== "all" && t._company !== companyFilter) return false;
    // Year/month filter
    if (covYear !== "all" && String(t.Year) !== String(covYear)) return false;
    if (covMonth !== "all" && String(t.Month) !== String(covMonth)) return false;
    // Type
    if (typeFilter !== "all" && t.Type !== typeFilter) return false;
    // Group filter
    if (groupFilter.length > 0) {
      const p = profileMap[(t._company || "") + "|" + t.Login];
      const grp = p?._clientGroup || "Unassigned";
      if (!groupFilter.includes(grp)) return false;
    }
    // Exclude days range
    if (exclDays.from > 0 && exclDays.to > 0 && t.EntryTime) {
      const d = new Date(t.EntryTime);
      const day = d.getDate();
      if (day >= exclDays.from && day <= exclDays.to) return false;
    }
    // Exclude
    const ls = String(t.Login).toLowerCase();
    if (exclSet.has(ls)) return false;
    const p = profileMap[(t._company || "") + "|" + t.Login];
    if (p && exclSet.has((p.Name || "").toLowerCase())) return false;
    return true;
  }), [CD.trades, typeFilter, exclSet, profileMap, companyFilter, covYear, covMonth, groupFilter, exclDays]);

  const clients = useMemo(() => {
    const map = {};
    filteredTrades.forEach(t => {
      const key = (t._company || "") + "|" + t.Login;
      if (!map[key]) {
        map[key] = { Login: t.Login, _company: t._company, key, tradeCount: 0, totalVolume: 0, actualProfit: 0, buyCount: 0, sellCount: 0, buyVolume: 0, sellVolume: 0, timePL: {}, pricePL: {} };
        TIME_KEYS.forEach(k => map[key].timePL[k] = 0); PRICE_KEYS.forEach(k => map[key].pricePL[k] = 0);
      }
      const c = map[key]; c.tradeCount++; c.totalVolume += safeNum(t.Volume); c.actualProfit += safeNum(t.Profit);
      if (t.Type === "buy") { c.buyCount++; c.buyVolume += safeNum(t.Volume); } else { c.sellCount++; c.sellVolume += safeNum(t.Volume); }
      TIME_KEYS.forEach(k => c.timePL[k] += safeNum(t["PL_" + k]));
      PRICE_KEYS.forEach(k => c.pricePL[k] += safeNum(t["PL_" + k]));
    });
    return Object.values(map).map(c => {
      const p = profileMap[c.key];
      c.Name = p?.Name || ""; c.AM = p?.["A.M"] || ""; c.Country = p?.Country || ""; c.CU = p?.CU || "";
      c.healthScore = p?.healthScore; c._clientGroup = p?._clientGroup;
      c.bestTime = findBest(c.timePL, TIME_KEYS); c.bestPrice = findBest(c.pricePL, PRICE_KEYS);
      c.bestTimePL = c.bestTime.pl; c.bestPricePL = c.bestPrice.pl;
      return c;
    });
  }, [filteredTrades, profileMap]);

  const totals = useMemo(() => {
    const t = { trades: 0, volume: 0, actualProfit: 0, timePL: {}, pricePL: {} };
    TIME_KEYS.forEach(k => t.timePL[k] = 0); PRICE_KEYS.forEach(k => t.pricePL[k] = 0);
    clients.forEach(c => { t.trades += c.tradeCount; t.volume += c.totalVolume; t.actualProfit += c.actualProfit; TIME_KEYS.forEach(k => t.timePL[k] += c.timePL[k]); PRICE_KEYS.forEach(k => t.pricePL[k] += c.pricePL[k]); });
    t.bestTime = findBest(t.timePL, TIME_KEYS); t.bestPrice = findBest(t.pricePL, PRICE_KEYS);
    return t;
  }, [clients]);

  const months = useMemo(() => [...new Set(CD.trades.map(t => t.Month))].sort(), [CD.trades]);
  const filterOpts = useMemo(() => ({
    countries: [...new Set(clients.map(c => c.Country).filter(Boolean))].sort(),
    ams: [...new Set(clients.map(c => c.AM).filter(Boolean))].sort(),
  }), [clients]);

  // Filtered clients for Clients tab
  const filteredClients = useMemo(() => clients.filter(c => {
    if (fCountry && c.Country !== fCountry) return false;
    if (fAM && c.AM !== fAM) return false;
    if (fSearch) { const s = fSearch.toLowerCase(); if (!String(c.Login).includes(s) && !(c.Name || "").toLowerCase().includes(s) && !(c.CU || "").toLowerCase().includes(s)) return false; }
    return true;
  }), [clients, fCountry, fAM, fSearch]);

  // Sorted clients
  const sortVal = (c, col) => col === "Name" || col === "AM" || col === "Country" ? (c[col] || "") : col === "bestTimePL" ? c.bestTimePL : col === "bestPricePL" ? c.bestPricePL : safeNum(c[col]);
  const sortedClients = useMemo(() => doSort(filteredClients, clientSort, sortVal), [filteredClients, clientSort]);

  // Sorted heatmap clients
  const hmSortedClients = useMemo(() => {
    const hmKeys = hmMode === "time" ? TIME_KEYS : PRICE_KEYS;
    const pf = hmMode === "time" ? "timePL" : "pricePL";
    return doSort(clients, hmSort, (c, col) => {
      if (col === "Login" || col === "Name") return c[col] || "";
      if (col === "actualProfit" || col === "totalVolume") return safeNum(c[col]);
      if (hmKeys.includes(col)) return c[pf][col] || 0;
      return safeNum(c[col]);
    });
  }, [clients, hmSort, hmMode]);

  const detailClient = selectedLogin ? clients.find(c => c.key === selectedLogin) : null;
  const activeFilters = [fCountry, fAM, fSearch].filter(Boolean).length;

  const tabDefs = [
    { id: "overview", label: "Overview" },
    { id: "heatmap", label: "Heatmap" },
    { id: "clients", label: "Clients", count: clients.length },
    { id: "best", label: "Best Covering" },
    { id: "exclude", label: "Exclude", count: (excludeList.length + (exclDays.from > 0 && exclDays.to > 0 ? 1 : 0)) || null },
    ...(months.length > 1 ? [{ id: "trends", label: "Trends" }] : []),
  ];

  const typeItems = [{ id: "all", label: "All", color: T.cyan }, { id: "buy", label: "Buy", color: T.green }, { id: "sell", label: "Sell", color: T.red }];
  const modeItems = [{ id: "time", label: "⏱ By Time" }, { id: "price", label: "$ By Price" }];
  const bestItems = [{ id: "time", label: "⏱ Time" }, { id: "price", label: "$ Price" }, { id: "both", label: "★ Best of Both" }];

  // Chart data
  const timeCD = TIME_KEYS.map(k => ({ name: TIME_LABELS[k], pl: totals.timePL[k], isBest: k === totals.bestTime.key }));
  const priceCD = PRICE_KEYS.map(k => ({ name: PRICE_LABELS[k], pl: totals.pricePL[k], isBest: k === totals.bestPrice.key }));

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#fbbf24", marginBottom: 2, display: "flex", alignItems: "center", gap: 8 }}>🥇 Covering Gold</h1>
          <span style={{ color: T.textDim, fontSize: 12 }}>{clients.length} clients • {totals.trades.toLocaleString()} trades • {months.join(", ")}{excludeList.length > 0 && ` • ${excludeList.length} excluded`}{exclDays.from > 0 && exclDays.to > 0 && ` • days ${exclDays.from}-${exclDays.to} excluded`}{groupFilter.length > 0 && ` • Groups: ${groupFilter.join(", ")}`}</span>
        </div>
        <Toggle items={typeItems} value={typeFilter} onChange={setTypeFilter} />
      </div>

      {/* Filter bar */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16, padding: "8px 12px", background: T.bgCard, borderRadius: 8, border: `1px solid ${T.border}30`, flexWrap: "wrap" }}>
        <span style={{ fontSize: 10, color: T.textMuted, fontWeight: 600, textTransform: "uppercase", marginRight: 4 }}>Filters:</span>
        <select value={covYear} onChange={e => setCovYear(e.target.value)} style={{ padding: "5px 10px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.bgInput, color: T.text, fontSize: 11 }}>
          <option value="all">All Years</option>
          {covOptions.years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={covMonth} onChange={e => setCovMonth(e.target.value)} style={{ padding: "5px 10px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.bgInput, color: T.text, fontSize: 11 }}>
          <option value="all">All Months</option>
          {covOptions.months.map(m => <option key={m} value={m}>{m}</option>)}
        </select>

        {/* Group multi-select */}
        <div style={{ display: "flex", gap: 3, alignItems: "center", borderLeft: `1px solid ${T.border}40`, paddingLeft: 8, marginLeft: 2, flexWrap: "wrap" }}>
          <span style={{ fontSize: 10, color: T.textMuted, fontWeight: 600, marginRight: 2 }}>Groups:</span>
          {availableGroups.map(g => {
            const sel = groupFilter.includes(g);
            const gc = { CRT_A: T.red, SPD_A: T.orange, HDG_A: T.purple, B2B_A: "#f59e0b", PRO_A: T.cyan, STD_AB: T.green, STD_B: T.blue, Unassigned: T.textMuted };
            const col = gc[g] || T.textMuted;
            return (
              <button key={g} onClick={() => setGroupFilter(prev => sel ? prev.filter(x => x !== g) : [...prev, g])}
                style={{ padding: "3px 8px", borderRadius: 5, border: `1px solid ${sel ? col : T.border}`, background: sel ? col + "20" : "transparent", color: sel ? col : T.textDim, fontSize: 10, fontWeight: 600, cursor: "pointer", transition: "all 0.15s" }}>
                {g}
              </button>
            );
          })}
          {groupFilter.length > 0 && (
            <button onClick={() => setGroupFilter([])} style={{ padding: "2px 6px", borderRadius: 4, border: `1px solid ${T.red}30`, background: T.red + "10", color: T.red, fontSize: 9, fontWeight: 600, cursor: "pointer", marginLeft: 2 }}>✕</button>
          )}
        </div>

        {companyFilter && companyFilter !== "all" && (
          <span style={{ padding: "3px 10px", borderRadius: 5, background: T.cyan + "15", border: `1px solid ${T.cyan}30`, color: T.cyan, fontSize: 10, fontWeight: 600 }}>
            {companyFilter === "salam" ? "Al Salam" : "River Prime"} only
          </span>
        )}
        {(covYear !== "all" || covMonth !== "all" || groupFilter.length > 0) && (
          <button onClick={() => { setCovYear("all"); setCovMonth("all"); setGroupFilter([]); }} style={{ padding: "4px 10px", borderRadius: 5, border: `1px solid ${T.red}30`, background: T.red + "10", color: T.red, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>Reset</button>
        )}
        <span style={{ marginLeft: "auto", fontSize: 10, color: T.textMuted }}>{filteredTrades.length.toLocaleString()} / {CD.trades.length.toLocaleString()} trades</span>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: `1px solid ${T.border}`, paddingBottom: 1 }}>
        {tabDefs.map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); setSelectedLogin(null); setTradePage(0); setNavHistory([]); }}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 18px", fontSize: 12, fontWeight: 600,
              background: tab === t.id ? T.bgCard : "transparent", color: tab === t.id ? "#fbbf24" : T.textDim,
              border: "none", borderBottom: tab === t.id ? "2px solid #fbbf24" : "2px solid transparent", cursor: "pointer", borderRadius: "8px 8px 0 0" }}>
            {t.label}
            {t.count != null && <span style={{ background: "#fbbf2420", color: "#fbbf24", padding: "1px 6px", borderRadius: 8, fontSize: 9, fontWeight: 700 }}>{t.count}</span>}
          </button>
        ))}
      </div>

      {/* ═══ OVERVIEW ═══ */}
      {tab === "overview" && (<div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12, marginBottom: 24 }}>
          <StatCard icon={<Activity size={18} />} label="Total Trades" value={totals.trades.toLocaleString()} color={T.cyan} />
          <StatCard icon={<Zap size={18} />} label="Total Volume" value={fmtNum(totals.volume, 1) + " lots"} color={T.purple} />
          <StatCard icon={<DollarSign size={18} />} label="Client Profit" value={fmtMoney(totals.actualProfit)} color={totals.actualProfit > 0 ? T.red : T.green} sub={totals.actualProfit > 0 ? "Broker lost this" : "Broker earned this"} />
          <StatCard icon={<Clock size={18} />} label="Best Time Cover" value={TIME_LABELS[totals.bestTime.key]} color="#fbbf24" sub={`Earns ${fmtMoney(totals.bestTime.pl)}`} />
          <StatCard icon={<DollarSign size={18} />} label="Best Price Cover" value={PRICE_LABELS[totals.bestPrice.key]} color={T.cyan} sub={`Earns ${fmtMoney(totals.bestPrice.pl)}`} />
          <StatCard icon={<Target size={18} />} label="Profitable Clients" value={`${clients.filter(c => c.bestTimePL > 0).length} / ${clients.length}`} color={T.green} sub="If covered optimally" />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
          {[{ title: "Covering By Time", data: timeCD, best: totals.bestTime, lbl: TIME_LABELS, ac: "#fbbf24" },
            { title: "Covering By Price", data: priceCD, best: totals.bestPrice, lbl: PRICE_LABELS, ac: T.cyan }].map(ch => (
            <Card key={ch.title} accent={ch.ac}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{ch.title}</span>
                <span style={{ fontSize: 10, color: T.textMuted }}>Best: <b style={{ color: T.green }}>{ch.lbl[ch.best.key]}</b> → {fmtMoney(ch.best.pl)}</span>
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={ch.data}><CartesianGrid strokeDasharray="3 3" stroke={T.border} /><XAxis dataKey="name" tick={{ fill: T.textDim, fontSize: 10 }} /><YAxis tick={{ fill: T.textDim, fontSize: 10 }} tickFormatter={fmtK} /><Tooltip content={<CTip />} /><ReferenceLine y={0} stroke={T.textMuted} strokeDasharray="3 3" />
                  <Bar dataKey="pl" name="Company PL" radius={[4,4,0,0]}>{ch.data.map((d, i) => <Cell key={i} fill={d.isBest ? "#fbbf24" : d.pl >= 0 ? T.green + "cc" : T.red + "cc"} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          ))}
        </div>
        <Card>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 12 }}>Top Clients by Volume</div>
          <div style={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{["","LOGIN","NAME","TRADES","VOLUME","CLIENT PROFIT","BEST TIME","TIME PL","BEST PRICE","PRICE PL"].map((h,i) => <th key={i} style={{ ...TH, textAlign: i === 2 ? "left" : "center", width: i === 0 ? 28 : undefined }}>{h}</th>)}</tr></thead>
            <tbody>{[...clients].sort((a,b) => b.totalVolume - a.totalVolume).slice(0, 10).map(c => (
              <tr key={c.key} style={{ borderBottom: `1px solid ${T.border}15` }} onMouseEnter={e => e.currentTarget.style.background = T.bgHover} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <td style={TD}><CoBadge co={c._company} size={18} /></td>
                <td style={TD}><span className="mono" onClick={() => goToClient(c.key, "overview")} style={{ color: T.cyan, fontWeight: 600, fontSize: 12, cursor: "pointer" }}>{c.Login}</span></td>
                <td style={{ ...TD, textAlign: "left", color: T.text, fontWeight: 500 }}>{(c.Name || "—").substring(0, 24)}</td>
                <td className="mono" style={{ ...TD, color: T.textDim }}>{c.tradeCount}</td>
                <td className="mono" style={{ ...TD, color: T.purple }}>{fmtNum(c.totalVolume, 1)}</td>
                <td className="mono" style={{ ...TD, color: c.actualProfit > 0 ? T.red : T.green, fontWeight: 600 }}>{fmtMoney(c.actualProfit)}</td>
                <td style={TD}><span style={{ padding: "3px 8px", borderRadius: 4, background: "#fbbf2420", color: "#fbbf24", fontSize: 10, fontWeight: 700 }}>{TIME_LABELS[c.bestTime.key]}</span></td>
                <td className="mono" style={{ ...TD, color: c.bestTimePL > 0 ? T.green : T.red, fontWeight: 700 }}>{fmtMoney(c.bestTimePL)}</td>
                <td style={TD}><span style={{ padding: "3px 8px", borderRadius: 4, background: T.cyan + "20", color: T.cyan, fontSize: 10, fontWeight: 700 }}>{PRICE_LABELS[c.bestPrice.key]}</span></td>
                <td className="mono" style={{ ...TD, color: c.bestPricePL > 0 ? T.green : T.red, fontWeight: 700 }}>{fmtMoney(c.bestPricePL)}</td>
              </tr>
            ))}</tbody>
          </table></div>
        </Card>
      </div>)}

      {/* ═══ HEATMAP (sortable) ═══ */}
      {tab === "heatmap" && (<div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Covering Heatmap <span style={{ fontSize: 11, color: T.textMuted, fontWeight: 400 }}>— click any column to sort</span></span>
            <ExportBtn count={hmSortedClients.length} onClick={() => {
              const keys = hmMode === "time" ? TIME_KEYS : PRICE_KEYS;
              const labels = hmMode === "time" ? TIME_LABELS : PRICE_LABELS;
              const pf = hmMode === "time" ? "timePL" : "pricePL";
              exportRawCSV(["Login", "Name", "Company", "Volume", "Client Profit", ...keys.map(k => labels[k])],
                hmSortedClients.map(c => [c.Login, c.Name || "", c._company || "", c.totalVolume?.toFixed(2), c.actualProfit?.toFixed(2), ...keys.map(k => (c[pf][k] || 0).toFixed(2))]),
                "CoveringGold-Heatmap");
            }} />
          </div>
          <Toggle items={modeItems} value={hmMode} onChange={v => { setHmMode(v); setHmSort({ col: "totalVolume", dir: "desc" }); }} />
        </div>
        <Card><div style={{ overflowX: "auto" }}>
          {(() => {
            const keys = hmMode === "time" ? TIME_KEYS : PRICE_KEYS;
            const labels = hmMode === "time" ? TIME_LABELS : PRICE_LABELS;
            const pf = hmMode === "time" ? "timePL" : "pricePL";
            let gMin = 0, gMax = 0;
            clients.forEach(c => keys.forEach(k => { const v = c[pf][k]; if (v < gMin) gMin = v; if (v > gMax) gMax = v; }));
            const range = Math.max(Math.abs(gMin), Math.abs(gMax)) || 1;
            return (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>
                  <SortHead label="LOGIN" col="Login" sort={hmSort} setSort={setHmSort} style={{ position: "sticky", left: 0, background: T.bgCard, zIndex: 2, minWidth: 70 }} />
                  <SortHead label="NAME" col="Name" sort={hmSort} setSort={setHmSort} style={{ textAlign: "left", position: "sticky", left: 70, background: T.bgCard, zIndex: 2, minWidth: 130 }} />
                  <SortHead label="VOLUME" col="totalVolume" sort={hmSort} setSort={setHmSort} style={{ minWidth: 70 }} />
                  <SortHead label="PROFIT" col="actualProfit" sort={hmSort} setSort={setHmSort} style={{ minWidth: 75 }} />
                  {keys.map(k => <SortHead key={k} label={labels[k]} col={k} sort={hmSort} setSort={setHmSort} style={{ minWidth: 62 }} />)}
                </tr></thead>
                <tbody>
                  {hmSortedClients.map(c => {
                    const best = hmMode === "time" ? c.bestTime : c.bestPrice;
                    return (<tr key={c.key}>
                      <td style={{ ...TD, position: "sticky", left: 0, background: T.bgCard, zIndex: 1 }}>
                        <span className="mono" onClick={() => goToClient(c.key, "heatmap")} style={{ color: T.cyan, fontWeight: 600, fontSize: 11, cursor: "pointer" }}>{c.Login}</span>
                      </td>
                      <td style={{ ...TD, textAlign: "left", fontSize: 11, color: T.text, position: "sticky", left: 70, background: T.bgCard, zIndex: 1, maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(c.Name || "—").substring(0, 18)}</td>
                      <td className="mono" style={{ ...TD, fontSize: 11, color: T.purple }}>{fmtK(c.totalVolume)}</td>
                      <td className="mono" style={{ ...TD, fontSize: 11, color: c.actualProfit > 0 ? T.red : T.green, fontWeight: 600 }}>{fmtK(c.actualProfit)}</td>
                      {keys.map(k => {
                        const v = c[pf][k], intensity = Math.min(Math.abs(v) / range, 1);
                        const bg = v > 0 ? `rgba(34,197,94,${0.08 + intensity * 0.45})` : v < 0 ? `rgba(239,68,68,${0.08 + intensity * 0.45})` : "transparent";
                        const isBest = k === best.key;
                        return <td key={k} style={{ padding: "6px 4px", textAlign: "center", background: bg, border: isBest ? "2px solid #fbbf24" : `1px solid ${T.border}15`, borderRadius: isBest ? 4 : 0 }}>
                          <span className="mono" style={{ fontSize: 10, color: T.text, fontWeight: isBest ? 800 : 500 }}>{fmtK(v)}</span>
                        </td>;
                      })}
                    </tr>);
                  })}
                  <tr style={{ borderTop: "2px solid #fbbf24" }}>
                    <td colSpan={2} style={{ ...TD, fontSize: 11, color: "#fbbf24", fontWeight: 700, position: "sticky", left: 0, background: T.bgCard, zIndex: 1, textAlign: "left", paddingLeft: 8 }}>TOTAL ({clients.length})</td>
                    <td className="mono" style={{ ...TD, fontSize: 11, color: T.purple, fontWeight: 700 }}>{fmtK(totals.volume)}</td>
                    <td className="mono" style={{ ...TD, fontSize: 11, color: totals.actualProfit > 0 ? T.red : T.green, fontWeight: 700 }}>{fmtK(totals.actualProfit)}</td>
                    {keys.map(k => { const v = totals[pf][k]; const isBest = k === (hmMode === "time" ? totals.bestTime : totals.bestPrice).key;
                      return <td key={k} className="mono" style={{ padding: "8px 4px", textAlign: "center", fontSize: 11, color: isBest ? "#fbbf24" : v > 0 ? T.green : T.red, fontWeight: 700, border: isBest ? "2px solid #fbbf24" : "none" }}>{fmtK(v)}</td>;
                    })}
                  </tr>
                </tbody>
              </table>
            );
          })()}
        </div></Card>
      </div>)}

      {/* ═══ CLIENTS LIST ═══ */}
      {tab === "clients" && !detailClient && (<div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
          <div style={{ position: "relative", flex: 1, minWidth: 200, maxWidth: 300 }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: 10, color: T.textMuted }} />
            <input value={fSearch} onChange={e => setFSearch(e.target.value)} placeholder="Search login, name, CU..." style={{ width: "100%", padding: "8px 10px 8px 30px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.bgInput, color: T.text, fontSize: 12, outline: "none" }} />
          </div>
          <select value={fCountry} onChange={e => setFCountry(e.target.value)} style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.bgInput, color: T.text, fontSize: 11 }}>
            <option value="">All Countries</option>{filterOpts.countries.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={fAM} onChange={e => setFAM(e.target.value)} style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.bgInput, color: T.text, fontSize: 11 }}>
            <option value="">All A.M</option>{filterOpts.ams.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          {activeFilters > 0 && <button onClick={() => { setFSearch(""); setFCountry(""); setFAM(""); }} style={{ padding: "6px 12px", borderRadius: 6, border: `1px solid ${T.red}40`, background: T.red + "15", color: T.red, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>Clear ({activeFilters})</button>}
          <ExportBtn count={sortedClients.length} onClick={() => exportRawCSV(
            ["Login", "Name", "Company", "Trades", "Volume", "Client Profit", "Best Time", "Time PL", "Best Price", "Price PL", "A.M"],
            sortedClients.map(c => [c.Login, c.Name || "", c._company || "", c.tradeCount, c.totalVolume?.toFixed(2), c.actualProfit?.toFixed(2), TIME_LABELS[c.bestTime.key], c.bestTimePL?.toFixed(2), PRICE_LABELS[c.bestPrice.key], c.bestPricePL?.toFixed(2), c.AM || ""]),
            "CoveringGold-Clients")} />
          <span style={{ fontSize: 11, color: T.textMuted, marginLeft: "auto" }}>{filteredClients.length} of {clients.length}</span>
        </div>
        <Card><div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1050 }}>
            <thead><tr>
              <th style={{ ...TH, width: 28 }}></th>
              <SortHead label="Login" col="Login" sort={clientSort} setSort={setClientSort} />
              <SortHead label="Name" col="Name" sort={clientSort} setSort={setClientSort} style={{ textAlign: "left" }} />
              <SortHead label="Trades" col="tradeCount" sort={clientSort} setSort={setClientSort} />
              <SortHead label="Volume" col="totalVolume" sort={clientSort} setSort={setClientSort} />
              <SortHead label="Client Profit" col="actualProfit" sort={clientSort} setSort={setClientSort} />
              <th style={TH}>BEST TIME</th>
              <SortHead label="Time PL" col="bestTimePL" sort={clientSort} setSort={setClientSort} />
              <th style={TH}>BEST PRICE</th>
              <SortHead label="Price PL" col="bestPricePL" sort={clientSort} setSort={setClientSort} />
              <SortHead label="A.M" col="AM" sort={clientSort} setSort={setClientSort} />
            </tr></thead>
            <tbody>{sortedClients.map(c => (
              <tr key={c.key} style={{ borderBottom: `1px solid ${T.border}15` }} onMouseEnter={e => e.currentTarget.style.background = T.bgHover} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <td style={TD}><CoBadge co={c._company} size={16} /></td>
                <td style={TD}><span className="mono" onClick={() => goToClient(c.key, "clients")} style={{ color: T.cyan, fontWeight: 600, fontSize: 12, cursor: "pointer" }}>{c.Login}</span></td>
                <td style={{ ...TD, textAlign: "left" }}><div onClick={() => goToClient(c.key, "clients")} style={{ cursor: "pointer" }}><div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{(c.Name || "—").substring(0, 24)}</div>{c.Country && <div style={{ fontSize: 9, color: T.textMuted }}>{c.Country}</div>}</div></td>
                <td className="mono" style={{ ...TD, color: T.textDim }}>{c.tradeCount}</td>
                <td className="mono" style={{ ...TD, color: T.purple, fontWeight: 600 }}>{fmtNum(c.totalVolume, 1)}</td>
                <td className="mono" style={{ ...TD, color: c.actualProfit > 0 ? T.red : T.green, fontWeight: 600 }}>{fmtMoney(c.actualProfit)}</td>
                <td style={TD}><span style={{ padding: "3px 8px", borderRadius: 4, background: "#fbbf2420", color: "#fbbf24", fontSize: 10, fontWeight: 700 }}>{TIME_LABELS[c.bestTime.key]}</span></td>
                <td className="mono" style={{ ...TD, color: c.bestTimePL > 0 ? T.green : T.red, fontWeight: 700 }}>{fmtMoney(c.bestTimePL)}</td>
                <td style={TD}><span style={{ padding: "3px 8px", borderRadius: 4, background: T.cyan + "20", color: T.cyan, fontSize: 10, fontWeight: 700 }}>{PRICE_LABELS[c.bestPrice.key]}</span></td>
                <td className="mono" style={{ ...TD, color: c.bestPricePL > 0 ? T.green : T.red, fontWeight: 700 }}>{fmtMoney(c.bestPricePL)}</td>
                <td style={{ ...TD, fontSize: 11, color: T.textDim }}>{(c.AM || "—").substring(0, 16)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div></Card>
      </div>)}

      {/* ═══ CLIENT DETAIL ═══ */}
      {tab === "clients" && detailClient && (() => {
        const dc = detailClient, prof = profileMap[dc.key];
        const cTrades = filteredTrades.filter(t => t.Login === dc.Login && t._company === dc._company);
        const buyT = cTrades.filter(t => t.Type === "buy"), sellT = cTrades.filter(t => t.Type === "sell");
        const agg = (tr, keys, px) => { const m = {}; keys.forEach(k => m[k] = 0); tr.forEach(t => keys.forEach(k => m[k] += safeNum(t[px + k]))); return m; };
        const buyTP = agg(buyT, TIME_KEYS, "PL_"), sellTP = agg(sellT, TIME_KEYS, "PL_");
        const buyPP = agg(buyT, PRICE_KEYS, "PL_"), sellPP = agg(sellT, PRICE_KEYS, "PL_");
        const buyBT = findBest(buyTP, TIME_KEYS), sellBT = findBest(sellTP, TIME_KEYS);
        const buyBP = findBest(buyPP, PRICE_KEYS), sellBP = findBest(sellPP, PRICE_KEYS);

        const dcTimeD = TIME_KEYS.map(k => ({ name: TIME_LABELS[k], pl: dc.timePL[k], isBest: k === dc.bestTime.key }));
        const dcPriceD = PRICE_KEYS.map(k => ({ name: PRICE_LABELS[k], pl: dc.pricePL[k], isBest: k === dc.bestPrice.key }));
        const bsK = bsChartMode === "time" ? TIME_KEYS : PRICE_KEYS, bsL = bsChartMode === "time" ? TIME_LABELS : PRICE_LABELS;
        const bsCD = bsK.map(k => ({ name: bsL[k], buy: (bsChartMode === "time" ? buyTP : buyPP)[k], sell: (bsChartMode === "time" ? sellTP : sellPP)[k] }));

        // Trade table
        const trK = detailTradeMode === "time" ? TIME_KEYS : PRICE_KEYS, trL = detailTradeMode === "time" ? TIME_LABELS : PRICE_LABELS;
        // Sort trades (no useMemo - inside conditional render)
        const sortedTrades = !tradeSort.col ? cTrades : doSort(cTrades, tradeSort, (t, col) => {
          if (col === "Type") return t.Type || "";
          if (col === "EntryTime") return t.EntryTime ? new Date(t.EntryTime).getTime() : 0;
          if (col === "Volume" || col === "Price" || col === "Profit") return safeNum(t[col]);
          return safeNum(t["PL_" + col]);
        });
        const PER = 50, totalPg = Math.ceil(sortedTrades.length / PER);
        const pgTr = sortedTrades.slice(tradePage * PER, (tradePage + 1) * PER);

        return (<div>
          <button onClick={goBack} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, border: `1px solid ${T.border}`, background: "transparent", color: T.textDim, cursor: "pointer", marginBottom: 16, fontSize: 13 }}>
            <ChevronLeft size={16} /> Back
          </button>

          {/* Client header - redesigned compact */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 16, marginBottom: 16 }}>
            <Card accent="#fbbf24">
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                <CoBadge co={dc._company} size={28} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 20, fontWeight: 700, color: T.text }}>{dc.Name || `Login ${dc.Login}`}</span>
                    <span className="mono" style={{ color: T.cyan, fontSize: 14 }}>{dc.Login}</span>
                    {prof && <span onClick={() => onClientClick?.(prof)} style={{ padding: "3px 10px", borderRadius: 5, background: T.cyan + "20", color: T.cyan, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>View Profile</span>}
                  </div>
                  {dc.AM && dc.AM !== "X" && <span style={{ fontSize: 11, color: T.textMuted }}>A.M: {dc.AM}</span>}
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10 }}>
                {[
                  { label: "Trades", value: dc.tradeCount, color: T.text },
                  { label: "Volume", value: fmtNum(dc.totalVolume, 2), color: T.purple },
                  { label: "Client Profit", value: fmtMoney(dc.actualProfit), color: dc.actualProfit > 0 ? T.red : T.green },
                  { label: "Buy", value: `${dc.buyCount} (${fmtNum(dc.buyVolume,1)} lots)`, color: T.green },
                  { label: "Sell", value: `${dc.sellCount} (${fmtNum(dc.sellVolume,1)} lots)`, color: T.red },
                ].map(s => (
                  <div key={s.label} style={{ background: T.bg, borderRadius: 8, padding: "14px 12px", textAlign: "center", border: `1px solid ${T.border}30` }}>
                    <div style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", fontWeight: 700, marginBottom: 6, letterSpacing: 0.5 }}>{s.label}</div>
                    <div className="mono" style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Recommendation box */}
            <Card accent="#fbbf24">
              <div style={{ fontSize: 11, color: "#fbbf24", fontWeight: 700, textTransform: "uppercase", marginBottom: 10, letterSpacing: 1 }}>⚡ RECOMMENDATION</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px", fontSize: 13, marginBottom: 10 }}>
                <div style={{ color: T.textMuted, fontSize: 11 }}>Time:</div>
                <div><span style={{ color: "#fbbf24", fontWeight: 700 }}>{TIME_LABELS[dc.bestTime.key]}</span> → <span className="mono" style={{ color: dc.bestTimePL > 0 ? T.green : T.red, fontWeight: 700 }}>{fmtMoney(dc.bestTimePL)}</span></div>
                <div style={{ color: T.textMuted, fontSize: 11 }}>Price:</div>
                <div><span style={{ color: T.cyan, fontWeight: 700 }}>{PRICE_LABELS[dc.bestPrice.key]}</span> → <span className="mono" style={{ color: dc.bestPricePL > 0 ? T.green : T.red, fontWeight: 700 }}>{fmtMoney(dc.bestPricePL)}</span></div>
              </div>
              <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 8, fontSize: 11 }}>
                <div style={{ color: T.green, marginBottom: 3 }}>Buy: {TIME_LABELS[buyBT.key]} ({fmtMoney(buyBT.pl)}) / {PRICE_LABELS[buyBP.key]} ({fmtMoney(buyBP.pl)})</div>
                <div style={{ color: T.red }}>Sell: {TIME_LABELS[sellBT.key]} ({fmtMoney(sellBT.pl)}) / {PRICE_LABELS[sellBP.key]} ({fmtMoney(sellBP.pl)})</div>
              </div>
            </Card>
          </div>

          {/* Charts */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <Card>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Covering Curve</span>
                <Toggle items={modeItems} value={detailChartMode} onChange={setDetailChartMode} />
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={detailChartMode === "time" ? dcTimeD : dcPriceD}><CartesianGrid strokeDasharray="3 3" stroke={T.border} /><XAxis dataKey="name" tick={{ fill: T.textDim, fontSize: 10 }} /><YAxis tick={{ fill: T.textDim, fontSize: 10 }} tickFormatter={fmtK} /><Tooltip content={<CTip />} /><ReferenceLine y={0} stroke={T.textMuted} />
                  <Bar dataKey="pl" name="Company PL" radius={[3,3,0,0]}>{(detailChartMode === "time" ? dcTimeD : dcPriceD).map((d, i) => <Cell key={i} fill={d.isBest ? "#fbbf24" : d.pl >= 0 ? T.green + "cc" : T.red + "cc"} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Buy vs Sell</span>
                <Toggle items={modeItems} value={bsChartMode} onChange={setBsChartMode} />
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={bsCD}><CartesianGrid strokeDasharray="3 3" stroke={T.border} /><XAxis dataKey="name" tick={{ fill: T.textDim, fontSize: 10 }} /><YAxis tick={{ fill: T.textDim, fontSize: 10 }} tickFormatter={fmtK} /><Tooltip content={<CTip />} /><ReferenceLine y={0} stroke={T.textMuted} />
                  <Line type="monotone" dataKey="buy" name="Buy" stroke={T.green} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="sell" name="Sell" stroke={T.red} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {/* Sortable trades table */}
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Individual Trades ({cTrades.length}) <span style={{ fontSize: 11, color: T.textMuted, fontWeight: 400 }}>— click column to sort</span></span>
                <ExportBtn count={cTrades.length} onClick={() => exportRawCSV(
                  ["Type", "Entry Time", "Volume", "Price", "Profit", ...trK.map(k => trL[k])],
                  cTrades.map(t => [t.Type, t.EntryTime || "", safeNum(t.Volume).toFixed(2), safeNum(t.Price).toFixed(2), safeNum(t.Profit).toFixed(2), ...trK.map(k => safeNum(t["PL_" + k]).toFixed(2))]),
                  `Trades-${dc.Login}`)} />
              </div>
              <Toggle items={modeItems} value={detailTradeMode} onChange={v => { setDetailTradeMode(v); setTradePage(0); setTradeSort({ col: null, dir: "desc" }); }} />
            </div>
            <div style={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <SortHead label="TYPE" col="Type" sort={tradeSort} setSort={setTradeSort} style={{ width: 50 }} />
                <SortHead label="ENTRY TIME" col="EntryTime" sort={tradeSort} setSort={setTradeSort} />
                <SortHead label="VOLUME" col="Volume" sort={tradeSort} setSort={setTradeSort} />
                <SortHead label="PRICE" col="Price" sort={tradeSort} setSort={setTradeSort} />
                <SortHead label="PROFIT" col="Profit" sort={tradeSort} setSort={setTradeSort} />
                {trK.map(k => <SortHead key={k} label={trL[k]} col={k} sort={tradeSort} setSort={setTradeSort} style={{ minWidth: 60 }} />)}
              </tr></thead>
              <tbody>{pgTr.map((t, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${T.border}10` }}>
                  <td style={TD}><span style={{ padding: "2px 7px", borderRadius: 4, background: t.Type === "buy" ? T.green + "20" : T.red + "20", color: t.Type === "buy" ? T.green : T.red, fontSize: 10, fontWeight: 700 }}>{t.Type}</span></td>
                  <td className="mono" style={{ ...TD, fontSize: 10, color: T.textDim }}>{t.EntryTime ? new Date(t.EntryTime).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                  <td className="mono" style={{ ...TD, fontSize: 11, color: T.purple }}>{safeNum(t.Volume).toFixed(2)}</td>
                  <td className="mono" style={{ ...TD, fontSize: 11, color: T.text }}>{safeNum(t.Price).toFixed(2)}</td>
                  <td className="mono" style={{ ...TD, fontSize: 11, color: safeNum(t.Profit) >= 0 ? T.red : T.green, fontWeight: 600 }}>{fmtMoney(safeNum(t.Profit))}</td>
                  {trK.map(k => { const v = safeNum(t["PL_" + k]); return <td key={k} className="mono" style={{ ...TD, fontSize: 10, color: v >= 0 ? T.green : T.red }}>{fmtMoney(v)}</td>; })}
                </tr>
              ))}</tbody>
            </table></div>
            {totalPg > 1 && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 8px", borderTop: `1px solid ${T.border}` }}>
                <span style={{ fontSize: 11, color: T.textMuted }}>{cTrades.length} trades</span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button disabled={tradePage === 0} onClick={() => setTradePage(p => p - 1)} style={{ padding: "4px 10px", borderRadius: 4, border: `1px solid ${T.border}`, background: "transparent", color: T.textDim, cursor: tradePage === 0 ? "default" : "pointer", opacity: tradePage === 0 ? 0.4 : 1, fontSize: 11 }}>‹ Prev</button>
                  <span className="mono" style={{ fontSize: 11, color: T.text }}>{tradePage + 1} / {totalPg}</span>
                  <button disabled={tradePage >= totalPg - 1} onClick={() => setTradePage(p => p + 1)} style={{ padding: "4px 10px", borderRadius: 4, border: `1px solid ${T.border}`, background: "transparent", color: T.textDim, cursor: tradePage >= totalPg - 1 ? "default" : "pointer", opacity: tradePage >= totalPg - 1 ? 0.4 : 1, fontSize: 11 }}>Next ›</button>
                </div>
              </div>
            )}
          </Card>
        </div>);
      })()}

      {/* ═══ BEST COVERING ═══ */}
      {tab === "best" && (() => {
        // Compute best for each client depending on mode
        const getClientBest = (c) => {
          if (bestMode === "time") return { interval: TIME_LABELS[c.bestTime.key], key: c.bestTime.key, pl: c.bestTimePL };
          if (bestMode === "price") return { interval: PRICE_LABELS[c.bestPrice.key], key: c.bestPrice.key, pl: c.bestPricePL };
          // "both" = pick whichever is higher
          if (c.bestTimePL >= c.bestPricePL) return { interval: TIME_LABELS[c.bestTime.key] + " (time)", key: "t_" + c.bestTime.key, pl: c.bestTimePL };
          return { interval: PRICE_LABELS[c.bestPrice.key] + " (price)", key: "p_" + c.bestPrice.key, pl: c.bestPricePL };
        };

        // Split: profitable (best PL > 0) vs never cover (all intervals negative)
        const profitable = [], neverCover = [];
        clients.forEach(c => {
          const b = getClientBest(c);
          if (b.pl > 0) profitable.push({ ...c, _bestPL: b.pl, _bestKey: b.key, _bestLabel: b.interval });
          else neverCover.push({ ...c, _bestPL: b.pl, _bestLabel: b.interval });
        });

        // Group profitable clients by interval
        const groupMap = {};
        profitable.forEach(c => {
          if (!groupMap[c._bestKey]) groupMap[c._bestKey] = { key: c._bestKey, label: c._bestLabel, clients: [], totalPL: 0, totalVol: 0 };
          groupMap[c._bestKey].clients.push(c);
          groupMap[c._bestKey].totalPL += c._bestPL;
          groupMap[c._bestKey].totalVol += c.totalVolume;
        });
        const groups = Object.values(groupMap).sort((a, b) => b.totalPL - a.totalPL);
        const grandPL = groups.reduce((s, g) => s + g.totalPL, 0);
        const neverVol = neverCover.reduce((s, c) => s + c.totalVolume, 0);
        const neverLoss = neverCover.reduce((s, c) => s + c._bestPL, 0);

        return (<div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>Best Covering</div>
              <div style={{ fontSize: 11, color: T.textDim }}>Each client at their optimal interval • Only profitable clients shown</div>
            </div>
            <Toggle items={bestItems} value={bestMode} onChange={v => { setBestMode(v); setBestExpanded(null); }} />
          </div>

          {/* KPI Cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 20 }}>
            <Card accent={T.green}>
              <div style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>Cover Clients</div>
              <div className="mono" style={{ fontSize: 22, fontWeight: 700, color: T.green }}>{profitable.length}</div>
              <div style={{ fontSize: 10, color: T.textDim, marginTop: 2 }}>of {clients.length} total</div>
            </Card>
            <Card accent="#fbbf24">
              <div style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>Total Profit</div>
              <div className="mono" style={{ fontSize: 22, fontWeight: 700, color: T.green }}>{fmtMoney(grandPL)}</div>
              <div style={{ fontSize: 10, color: T.textDim, marginTop: 2 }}>from covering</div>
            </Card>
            <Card accent={T.purple}>
              <div style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>Cover Volume</div>
              <div className="mono" style={{ fontSize: 22, fontWeight: 700, color: T.purple }}>{fmtNum(groups.reduce((s,g)=>s+g.totalVol,0), 1)}</div>
              <div style={{ fontSize: 10, color: T.textDim, marginTop: 2 }}>lots to cover</div>
            </Card>
            <Card accent={T.red}>
              <div style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>Never Cover</div>
              <div className="mono" style={{ fontSize: 22, fontWeight: 700, color: T.red }}>{neverCover.length}</div>
              <div style={{ fontSize: 10, color: T.textDim, marginTop: 2 }}>all intervals loss</div>
            </Card>
            <Card accent={T.red}>
              <div style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>Loss if Covered</div>
              <div className="mono" style={{ fontSize: 22, fontWeight: 700, color: T.red }}>{fmtMoney(neverLoss)}</div>
              <div style={{ fontSize: 10, color: T.textDim, marginTop: 2 }}>{fmtNum(neverVol, 1)} lots</div>
            </Card>
          </div>

          {/* Profitable groups table */}
          {profitable.length > 0 && (
            <Card style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.green, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>✅ Cover These Clients ({profitable.length})
                <ExportBtn count={profitable.length} onClick={() => exportRawCSV(
                  ["Login", "Name", "Company", "Optimal Interval", "Trades", "Volume", "Client Profit", "Company Profit"],
                  profitable.map(c => [c.Login, c.Name || "", c._company || "", c._bestLabel, c.tradeCount, c.totalVolume?.toFixed(2), c.actualProfit?.toFixed(2), c._bestPL?.toFixed(2)]),
                  "BestCovering-Profitable")} />
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>
                  <th style={{ ...TH, width: 30 }}></th>
                  <th style={{ ...TH, textAlign: "left" }}>COVER AT</th>
                  <th style={TH}>CLIENTS</th>
                  <th style={TH}>VOLUME</th>
                  <th style={TH}>COMPANY PROFIT</th>
                  <th style={{ ...TH, width: 80 }}>SHARE</th>
                </tr></thead>
                <tbody>
                  {groups.map(g => {
                    const isOpen = bestExpanded === g.key;
                    const pct = grandPL > 0 ? (g.totalPL / grandPL * 100) : 0;
                    return (<React.Fragment key={g.key}>
                      <tr onClick={() => setBestExpanded(isOpen ? null : g.key)} style={{ cursor: "pointer", borderBottom: `1px solid ${T.border}15`, background: isOpen ? T.bgHover : "transparent" }}
                        onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = T.bgHover; }} onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = "transparent"; }}>
                        <td style={TD}>{isOpen ? <ChevronDown size={14} color="#fbbf24" /> : <ChevronRight size={14} color={T.textMuted} />}</td>
                        <td style={{ ...TD, textAlign: "left" }}><span style={{ padding: "4px 12px", borderRadius: 5, background: "#fbbf2420", color: "#fbbf24", fontSize: 13, fontWeight: 700 }}>{g.label}</span></td>
                        <td className="mono" style={{ ...TD, fontSize: 13, fontWeight: 600, color: T.text }}>{g.clients.length}</td>
                        <td className="mono" style={{ ...TD, fontSize: 12, color: T.purple }}>{fmtNum(g.totalVol, 1)}</td>
                        <td className="mono" style={{ ...TD, fontSize: 14, fontWeight: 700, color: T.green }}>{fmtMoney(g.totalPL)}</td>
                        <td style={TD}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <div style={{ flex: 1, height: 6, background: T.bg, borderRadius: 3, overflow: "hidden" }}>
                              <div style={{ height: "100%", width: Math.max(pct, 2) + "%", background: T.green, borderRadius: 3 }} />
                            </div>
                            <span className="mono" style={{ fontSize: 10, color: T.textDim, minWidth: 32 }}>{pct.toFixed(0)}%</span>
                          </div>
                        </td>
                      </tr>
                      {isOpen && g.clients.sort((a, b) => b._bestPL - a._bestPL).map(c => (
                        <tr key={c.key} style={{ background: T.bg + "80", borderBottom: `1px solid ${T.border}10` }}>
                          <td style={TD}><CoBadge co={c._company} size={14} /></td>
                          <td style={{ ...TD, textAlign: "left" }}>
                            <span className="mono" onClick={() => goToClient(c.key, "best")} style={{ color: T.cyan, fontWeight: 600, fontSize: 11, cursor: "pointer", marginRight: 8 }}>{c.Login}</span>
                            <span style={{ fontSize: 11, color: T.text }}>{(c.Name || "").substring(0, 22)}</span>
                          </td>
                          <td className="mono" style={{ ...TD, fontSize: 11, color: T.textDim }}>{c.tradeCount}</td>
                          <td className="mono" style={{ ...TD, fontSize: 11, color: T.purple }}>{fmtNum(c.totalVolume, 1)}</td>
                          <td className="mono" style={{ ...TD, fontSize: 12, color: T.green, fontWeight: 700 }}>{fmtMoney(c._bestPL)}</td>
                          <td className="mono" style={{ ...TD, fontSize: 11, color: c.actualProfit > 0 ? T.red : T.green }}>{fmtMoney(c.actualProfit)}</td>
                        </tr>
                      ))}
                    </React.Fragment>);
                  })}
                  <tr style={{ borderTop: "2px solid #fbbf24" }}>
                    <td style={TD}></td>
                    <td style={{ ...TD, textAlign: "left", fontWeight: 700, color: "#fbbf24", fontSize: 13 }}>TOTAL PROFIT</td>
                    <td className="mono" style={{ ...TD, fontWeight: 700, color: T.text }}>{profitable.length}</td>
                    <td className="mono" style={{ ...TD, fontWeight: 700, color: T.purple }}>{fmtNum(groups.reduce((s,g) => s + g.totalVol, 0), 1)}</td>
                    <td className="mono" style={{ ...TD, fontSize: 16, fontWeight: 700, color: T.green }}>{fmtMoney(grandPL)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </Card>
          )}

          {/* Never Cover table */}
          {neverCover.length > 0 && (
            <Card>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.red, display: "flex", alignItems: "center", gap: 6 }}>
                  🚫 Never Cover — All Intervals Are Loss ({neverCover.length})
                  <ExportBtn count={neverCover.length} onClick={() => exportRawCSV(
                    ["Login", "Name", "Company", "Trades", "Volume", "Client Profit", "Best Possible PL", "Best At"],
                    neverCover.sort((a,b) => a._bestPL - b._bestPL).map(c => [c.Login, c.Name || "", c._company || "", c.tradeCount, c.totalVolume?.toFixed(2), c.actualProfit?.toFixed(2), c._bestPL?.toFixed(2), c._bestLabel]),
                    "BestCovering-NeverCover")} />
                </div>
                <span className="mono" style={{ fontSize: 12, color: T.red }}>Best possible: {fmtMoney(neverLoss)} • {fmtNum(neverVol, 1)} lots</span>
              </div>
              <div style={{ fontSize: 11, color: T.textDim, marginBottom: 10 }}>These clients lose money for the company at every covering interval. Do not cover them.</div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>
                  <th style={{ ...TH, width: 28 }}></th>
                  <th style={{ ...TH, textAlign: "center" }}>LOGIN</th>
                  <th style={{ ...TH, textAlign: "left" }}>NAME</th>
                  <th style={TH}>TRADES</th>
                  <th style={TH}>VOLUME</th>
                  <th style={TH}>CLIENT PROFIT</th>
                  <th style={TH}>BEST POSSIBLE PL</th>
                  <th style={TH}>BEST AT</th>
                </tr></thead>
                <tbody>
                  {neverCover.sort((a, b) => a._bestPL - b._bestPL).map(c => (
                    <tr key={c.key} style={{ borderBottom: `1px solid ${T.border}10` }}>
                      <td style={TD}><CoBadge co={c._company} size={14} /></td>
                      <td style={TD}><span className="mono" onClick={() => goToClient(c.key, "best")} style={{ color: T.cyan, fontWeight: 600, fontSize: 11, cursor: "pointer" }}>{c.Login}</span></td>
                      <td style={{ ...TD, textAlign: "left", fontSize: 11, color: T.text }}>{(c.Name || "—").substring(0, 22)}</td>
                      <td className="mono" style={{ ...TD, fontSize: 11, color: T.textDim }}>{c.tradeCount}</td>
                      <td className="mono" style={{ ...TD, fontSize: 11, color: T.purple }}>{fmtNum(c.totalVolume, 1)}</td>
                      <td className="mono" style={{ ...TD, fontSize: 11, color: c.actualProfit > 0 ? T.red : T.green }}>{fmtMoney(c.actualProfit)}</td>
                      <td className="mono" style={{ ...TD, fontSize: 12, color: T.red, fontWeight: 700 }}>{fmtMoney(c._bestPL)}</td>
                      <td style={TD}><span style={{ padding: "2px 8px", borderRadius: 4, background: T.red + "15", color: T.red, fontSize: 10, fontWeight: 600 }}>{c._bestLabel}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>);
      })()}

      {/* ═══ EXCLUDE ═══ */}
      {tab === "exclude" && (<div>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 4 }}>Exclude Clients</div>
        <div style={{ fontSize: 11, color: T.textDim, marginBottom: 20 }}>Excluded clients are removed from all tables, charts, and calculations.</div>

        {/* Exclude Days Range */}
        <Card style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
            <Calendar size={16} color={T.amber} /> Exclude Days of Month
          </div>
          <div style={{ fontSize: 11, color: T.textDim, marginBottom: 12 }}>Remove trades that opened on specific days of each month (e.g., days 1-5 to skip early-month activity).</div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <span style={{ fontSize: 12, color: T.textDim }}>From day</span>
            <input type="number" min={0} max={31} value={exclDays.from || ""} placeholder="0"
              onChange={e => updateExclDays({ ...exclDays, from: parseInt(e.target.value) || 0 })}
              style={{ width: 60, padding: "6px 10px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.bgInput, color: T.text, fontSize: 13, textAlign: "center" }} />
            <span style={{ fontSize: 12, color: T.textDim }}>to day</span>
            <input type="number" min={0} max={31} value={exclDays.to || ""} placeholder="0"
              onChange={e => updateExclDays({ ...exclDays, to: parseInt(e.target.value) || 0 })}
              style={{ width: 60, padding: "6px 10px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.bgInput, color: T.text, fontSize: 13, textAlign: "center" }} />
            {exclDays.from > 0 && exclDays.to > 0 && (<>
              <span style={{ fontSize: 12, color: T.amber, fontWeight: 600 }}>
                Excluding days {exclDays.from}–{exclDays.to}
              </span>
              <button onClick={() => updateExclDays({ from: 0, to: 0 })}
                style={{ padding: "4px 10px", borderRadius: 5, border: `1px solid ${T.red}30`, background: T.red + "10", color: T.red, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>Clear</button>
            </>)}
          </div>
        </Card>

        <Card style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 12 }}>Add to Exclude List</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <input value={exclInput} onChange={e => setExclInput(e.target.value)} placeholder="Type login number or client name to exclude or filter list below..."
              onKeyDown={e => { if (e.key === "Enter" && exclInput.trim()) { updateExclude([...excludeList, exclInput.trim()]); setExclInput(""); } }}
              style={{ flex: 1, padding: "10px 14px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.bgInput, color: T.text, fontSize: 13, outline: "none" }} />
            <button onClick={() => { if (exclInput.trim()) { updateExclude([...excludeList, exclInput.trim()]); setExclInput(""); } }}
              style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: T.red, color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
              <Plus size={14} /> Exclude
            </button>
          </div>
          {/* Quick add — filtered by input */}
          {(() => {
            const q = exclInput.toLowerCase().trim();
            const available = clients.filter(c => {
              if (exclSet.has(String(c.Login).toLowerCase())) return false;
              if (!q) return true;
              return String(c.Login).includes(q) || (c.Name || "").toLowerCase().includes(q) || (c.CU || "").toLowerCase().includes(q);
            });
            return available.length > 0 && (<>
              <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 4 }}>Click to exclude ({available.length} available{q ? `, filtered by "${exclInput}"` : ""}):</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, maxHeight: 140, overflowY: "auto" }}>
                {available.slice(0, 60).map(c => (
                  <button key={c.key} onClick={() => updateExclude([...excludeList, String(c.Login)])}
                    style={{ padding: "4px 8px", borderRadius: 4, border: `1px solid ${T.border}`, background: "transparent", color: T.textDim, fontSize: 10, cursor: "pointer" }}
                    onMouseEnter={e => { e.currentTarget.style.background = T.red + "20"; e.currentTarget.style.borderColor = T.red; e.currentTarget.style.color = T.red; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textDim; }}>
                    {c.Login} {c.Name ? `(${c.Name.substring(0,14)})` : ""}
                  </button>
                ))}
              </div>
            </>);
          })()}
        </Card>

        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Excluded ({excludeList.length})</span>
            {excludeList.length > 0 && <button onClick={() => updateExclude([])} style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${T.red}40`, background: T.red + "15", color: T.red, fontSize: 10, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}><Trash2 size={12} /> Clear All</button>}
          </div>
          {excludeList.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 20px", color: T.textMuted, fontSize: 12 }}>No clients excluded.</div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {excludeList.map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 6, background: T.red + "15", border: `1px solid ${T.red}30` }}>
                  <span style={{ fontSize: 12, color: T.red, fontWeight: 600 }}>{item}</span>
                  <button onClick={() => updateExclude(excludeList.filter((_, j) => j !== i))} style={{ background: "none", border: "none", color: T.red, cursor: "pointer", padding: 0, display: "flex" }}><X size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>)}

      {/* ═══ TRENDS ═══ */}
      {tab === "trends" && (<div>
        {months.length < 2 ? <Card><div style={{ textAlign: "center", padding: "60px 20px" }}><div style={{ fontSize: 48, marginBottom: 16 }}>📈</div><div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>Upload 2+ Months for Trends</div></div></Card>
        : (() => {
          const md = months.map(month => {
            const mt = filteredTrades.filter(t => t.Month === month);
            const mTP = {}; TIME_KEYS.forEach(k => { mTP[k] = 0; mt.forEach(t => mTP[k] += safeNum(t["PL_" + k])); });
            const mPP = {}; PRICE_KEYS.forEach(k => { mPP[k] = 0; mt.forEach(t => mPP[k] += safeNum(t["PL_" + k])); });
            return { month, trades: mt.length, volume: mt.reduce((s, t) => s + safeNum(t.Volume), 0), profit: mt.reduce((s, t) => s + safeNum(t.Profit), 0), bestTime: findBest(mTP, TIME_KEYS), bestPrice: findBest(mPP, PRICE_KEYS) };
          });
          const tc = md.map(m => ({ name: m.month, bestTimePL: m.bestTime.pl, bestPricePL: m.bestPrice.pl, clientProfit: m.profit }));
          return (<>
            <Card style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 12 }}>Monthly Trend</div>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={tc}><CartesianGrid strokeDasharray="3 3" stroke={T.border} /><XAxis dataKey="name" tick={{ fill: T.textDim, fontSize: 10 }} /><YAxis tick={{ fill: T.textDim, fontSize: 10 }} tickFormatter={fmtK} /><Tooltip content={<CTip />} /><ReferenceLine y={0} stroke={T.textMuted} />
                  <Line type="monotone" dataKey="bestTimePL" name="Best Time PL" stroke="#fbbf24" strokeWidth={2} />
                  <Line type="monotone" dataKey="bestPricePL" name="Best Price PL" stroke={T.cyan} strokeWidth={2} />
                  <Line type="monotone" dataKey="clientProfit" name="Client Profit" stroke={T.red} strokeWidth={1} strokeDasharray="5 5" />
                </LineChart>
              </ResponsiveContainer>
            </Card>
            <Card><table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>{["MONTH","TRADES","VOLUME","CLIENT PROFIT","BEST TIME","TIME PL","BEST PRICE","PRICE PL"].map((h,i) => <th key={i} style={{ ...TH, textAlign: i === 0 ? "left" : "center" }}>{h}</th>)}</tr></thead>
              <tbody>{md.map(m => (
                <tr key={m.month} style={{ borderBottom: `1px solid ${T.border}15` }}>
                  <td style={{ ...TD, textAlign: "left", fontSize: 12, fontWeight: 600, color: T.text }}>{m.month}</td>
                  <td className="mono" style={{ ...TD, color: T.textDim }}>{m.trades.toLocaleString()}</td>
                  <td className="mono" style={{ ...TD, color: T.purple }}>{fmtNum(m.volume, 1)}</td>
                  <td className="mono" style={{ ...TD, color: m.profit > 0 ? T.red : T.green, fontWeight: 600 }}>{fmtMoney(m.profit)}</td>
                  <td style={TD}><span style={{ padding: "3px 8px", borderRadius: 4, background: "#fbbf2420", color: "#fbbf24", fontSize: 10, fontWeight: 700 }}>{TIME_LABELS[m.bestTime.key]}</span></td>
                  <td className="mono" style={{ ...TD, color: m.bestTime.pl > 0 ? T.green : T.red, fontWeight: 600 }}>{fmtMoney(m.bestTime.pl)}</td>
                  <td style={TD}><span style={{ padding: "3px 8px", borderRadius: 4, background: T.cyan + "20", color: T.cyan, fontSize: 10, fontWeight: 700 }}>{PRICE_LABELS[m.bestPrice.key]}</span></td>
                  <td className="mono" style={{ ...TD, color: m.bestPrice.pl > 0 ? T.green : T.red, fontWeight: 600 }}>{fmtMoney(m.bestPrice.pl)}</td>
                </tr>
              ))}</tbody>
            </table></Card>
          </>);
        })()}
      </div>)}
    </div>
  );
}
